import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('arti24-credit')
export class Arti24Credit extends LitElement {
    /**
     * Pozostawiamy komponent w light DOM,
     * zgodnie z pozostałymi komponentami projektu.
     */
    protected createRenderRoot(): HTMLElement {
        return this;
    }

    @property({ type: String, attribute: 'href' })
    href = 'https://arti24.eu/';

    // The badge only ever renders this at 16-18 CSS px (see .arti24-credit__icon
    // below) - ".48." asks the picture pipeline for a 48px-wide variant
    // (comfortable 2-3x DPR headroom) instead of the 512x512 original, which
    // Lighthouse flagged as ~244 KiB downloaded for an 18px icon.
    @property({ type: String, attribute: 'icon-src' })
    // An existing variant: logo-A.48.png does not exist and answered 301 to this file.
    iconSrc = 'https://arti24.eu/pictures/logo/logo-A.160.png';

    /**
     * Teksty przychodzą gotowe z LD (namespace arti24-credit, patrz
     * apps/_common_/views/templates/layout/arti24-credit.ejs) — komponent nie zna języka.
     */
    @property({ type: String })
    label = '';

    @property({ type: String, attribute: 'accessible-label' })
    accessibleLabel = '';

    @property({ type: Boolean, reflect: true })
    compact = false;

    /**
     * Widoczna etykieta przed ikoną ("Realizacja: [A] Arti24"). Widoczny
     * tekst jest wtedy nazwą dostępną linku, więc bez aria-label.
     */
    @property({ type: Boolean, attribute: 'label-first', reflect: true })
    labelFirst = false;

    render() {
        return html`
            <style>
                arti24-credit {
                    display: inline-flex;
                    max-width: 100%;
                }

                arti24-credit .arti24-credit {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.42rem;

                    /* var(--arti24-color-text-muted) jest już dostrojony pod
                       oba motywy (patrz apps/arti24.eu/.../themes/_theme.scss);
                       samo "inherit" + stała nieprzezroczystość 0.76 dawało
                       słaby kontrast na ciemnym tle stopki. */
                    color: var(--arti24-color-text-muted, currentColor);
                    font-size: 0.78rem;
                    line-height: 1.2;
                    text-decoration: none;

                    transition:
                        color 160ms ease,
                        transform 160ms ease;
                }

                arti24-credit .arti24-credit:hover,
                arti24-credit .arti24-credit:focus-visible {
                    color: var(--arti24-color-text, currentColor);
                }

                arti24-credit .arti24-credit:hover {
                    transform: translateY(-1px);
                }

                arti24-credit .arti24-credit:focus-visible {
                    outline: 2px solid currentColor;
                    outline-offset: 4px;
                    border-radius: 0.25rem;
                }

                arti24-credit .arti24-credit__icon {
                    display: block;

                    width: 18px;
                    height: 18px;
                    flex: 0 0 18px;

                    object-fit: contain;
                }

                /* Sam jasny obrys nie wystarczał — środek litery to gradient
                   niebieskiego, a jego najciemniejsze partie nadal ginęły w
                   prawie czarnym tle stopki przy 18px. Zamiast dobierać kolejny
                   wariant pliku, w motywie ciemnym spłaszczamy całą ikonę do
                   jednolitej bieli filtrem CSS — gwarantuje to maksymalny
                   kontrast niezależnie od tego, jakie odcienie ma źródłowy PNG. */
                body.theme-dark arti24-credit .arti24-credit__icon,
                html[data-theme='dark'] body arti24-credit .arti24-credit__icon,
                :root[data-theme='dark'] arti24-credit .arti24-credit__icon {
                    filter: brightness(0) invert(1);
                }

                arti24-credit .arti24-credit__text {
                    white-space: nowrap;
                }

                arti24-credit .arti24-credit__brand {
                    font-weight: 600;
                }

                arti24-credit[compact] .arti24-credit__label {
                    display: none;
                }

                arti24-credit[compact] .arti24-credit {
                    gap: 0.3rem;
                }

                arti24-credit[label-first] .arti24-credit {
                    gap: 0.35rem;
                }

                @media (max-width: 480px) {
                    arti24-credit .arti24-credit {
                        font-size: 0.74rem;
                    }

                    arti24-credit .arti24-credit__icon {
                        width: 16px;
                        height: 16px;
                        flex-basis: 16px;
                    }
                }
            </style>

            ${this.labelFirst ? this.renderLabelFirst() : this.renderBadge()}
        `;
    }

    private renderIcon() {
        return html`<img
            class="arti24-credit__icon"
            src=${this.iconSrc}
            alt=""
            width="18"
            height="18"
            loading="lazy"
            decoding="async"
        />`;
    }

    private renderBadge() {
        return html`
            <a
                class="arti24-credit"
                href=${this.href}
                rel="nofollow"
                aria-label=${this.accessibleLabel}
                title=${this.accessibleLabel}
            >
                ${this.renderIcon()}

                <span class="arti24-credit__text">
                    <span class="arti24-credit__label">${this.label}</span>

                    <span class="arti24-credit__brand">Arti24</span>
                </span>
            </a>
        `;
    }

    private renderLabelFirst() {
        return html`
            <a class="arti24-credit" href=${this.href} rel="nofollow" title=${this.accessibleLabel}>
                <span class="arti24-credit__label arti24-credit__text">${this.label}</span>
                ${this.renderIcon()}
                <span class="arti24-credit__brand arti24-credit__text">Arti24</span>
            </a>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'arti24-credit': Arti24Credit;
    }
}
